import { Link } from "react-router-dom";
import { ShieldCheck, Lock, Users, ArrowRight, MessageSquare, Calendar, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import usePageMeta from "../lib/usePageMeta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1758691737278-3af15b37af48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxzdXBwb3J0aXZlJTIwd29ya3BsYWNlJTIwY29udmVyc2F0aW9ufGVufDB8fHx8MTc3ODI4ODk5OXww&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1758519288298-f0ceff4ccaa1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxzdXBwb3J0aXZlJTIwd29ya3BsYWNlJTIwY29udmVyc2F0aW9ufGVufDB8fHx8MTc3ODI4ODk5OXww&ixlib=rb-4.1.0&q=85";

const SUPPORT_AREAS = [
  { title: "Stress & burnout", body: "Recover before exhaustion sets in." },
  { title: "Grief & loss", body: "Quiet, steady support through hard seasons." },
  { title: "Workplace concerns", body: "Think out loud with someone neutral." },
  { title: "Family stress", body: "Marriage, parenting, and caregiving." },
  { title: "Life transitions", body: "Career shifts, moves, new chapters." },
  { title: "Crisis support", body: "Show up quickly when something serious happens." },
];

export default function HomePage() {
  usePageMeta({
    title: "CCCHAPS Care Portal | California Corporate Chaplains",
    description: "A confidential workplace chaplaincy support portal by California Corporate Chaplains — non-clinical emotional, grief, and workplace support for employees of enrolled organizations.",
  });
  const [scheduleUrl, setScheduleUrl] = useState("");

  useEffect(() => {
    api.get("/config").then((r) => setScheduleUrl(r.data?.schedule_chaplain_url || "")).catch(() => {});
  }, []);

  const scheduleTarget = scheduleUrl || "/care-portal/request-support";
  const scheduleExternal = Boolean(scheduleUrl);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F8FAFC] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-16 md:pb-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F0F4F8] text-[#3B597B] text-xs font-medium mb-6">
              <Lock className="w-3.5 h-3.5" strokeWidth={2} />
              Confidential workplace chaplaincy support
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 text-balance">
              Someone steady to talk to —{" "}
              <span className="text-[#4A6FA5]">when work and life feel heavy.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
              CCCHAPS Care Portal connects employees of enrolled companies with a confidential chaplain
              for stress, grief, burnout, family stress, leadership pressure, and crisis support. Chaplain
              support is available 24/7 on-call. Chaplain conversations are confidential within applicable
              ethical and legal limits. CCCHAPS documents service activity, not the private content of your
              conversations, and routine employer reports do not include employee names or written accounts of
              confidential conversations.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/care-portal/request-support"
                data-testid="hero-request-support-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors duration-200"
              >
                Request Confidential Support
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={scheduleTarget}
                target={scheduleExternal ? "_blank" : undefined}
                rel={scheduleExternal ? "noreferrer" : undefined}
                data-testid="hero-schedule-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors duration-200"
              >
                <Calendar className="w-4 h-4" />
                Schedule a Chaplain
              </a>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#4A6FA5]" /> Confidential</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#4A6FA5]" /> Employer-sponsored</span>
              <span className="flex items-center gap-2" data-testid="hero-chip-247"><MessageSquare className="w-4 h-4 text-[#4A6FA5]" /> 24/7 on-call</span>
              <span className="hidden sm:flex items-center gap-2" data-testid="hero-chip-non-clinical"><MessageSquare className="w-4 h-4 text-[#4A6FA5]" /> Confidential, Non-Clinical Support</span>
            </div>
            <p
              data-testid="care-portal-hero-crisis-notice"
              className="mt-5 text-xs text-slate-500 leading-relaxed max-w-xl"
            >
              CCCHAPS provides confidential, non-clinical chaplain support. If you are experiencing a life-threatening emergency, call{" "}
              <span className="font-semibold text-slate-700">911</span>.
            </p>
          </div>

          <div className="relative fade-up-delay-2">
            <div className="aspect-[5/6] rounded-2xl overflow-hidden shadow-xl border border-slate-200">
              <img src={HERO_IMG} alt="Two colleagues having a supportive conversation" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden sm:block bg-white rounded-2xl shadow-lg border border-slate-200 p-5 w-64">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-medium mb-2">
                <Lock className="w-3.5 h-3.5" /> Our Confidentiality Commitment
              </div>
              <p className="text-sm text-slate-700 leading-relaxed">
                Chaplain conversations are confidential within applicable ethical and legal limits, including legally
                required disclosures and serious safety concerns. Employers receive only aggregate, non-identifying
                utilization reports.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Support areas */}
      <section className="py-16 md:py-24 bg-white" data-testid="support-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">What chaplains help with</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              Support for the parts of life that don't fit into a meeting.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Chaplains are not therapists or counselors — they are trained, confidential listeners who walk alongside employees through everyday and extraordinary moments.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SUPPORT_AREAS.map((s) => (
              <div key={s.title} className="bg-white border border-slate-200 rounded-xl p-6 hover:border-[#4A6FA5] hover:shadow-sm transition-all duration-200" data-testid={`support-area-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <h3 className="font-heading text-lg font-medium text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALLACHAP card — public link only, no auth, no Care Portal data connection */}
      <section className="py-14 md:py-20 bg-[#F8FAFC]" data-testid="care-portal-callachap-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div className="hidden md:flex items-center justify-center w-16 h-16 rounded-2xl bg-[#4A6FA5]/10 text-[#4A6FA5] shrink-0">
              <Phone className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">On-demand employee support</p>
              <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">CALLACHAP</h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">
                Need someone to talk with? Connect with chaplain support by call, text, video, or email.
              </p>
            </div>
            <div className="md:ml-auto">
              <Link
                to="/callachap"
                data-testid="care-portal-callachap-cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors duration-200"
              >
                Explore CALLACHAP <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Confidentiality */}
      <section className="py-16 md:py-24 bg-[#F3F5F7]" data-testid="confidentiality-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <img src={ABOUT_IMG} alt="Two professionals talking in a modern office" className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">Confidentiality notice</p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
              What you share is handled with care and discretion.
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Information submitted through this portal is used to connect the employee with chaplaincy support. CCCHAPS is an independent, employee-centered support service — our chaplains are not employee investigators or an extension of HR, and CCCHAPS is not an employee reporting or monitoring agency. Chaplains document service activity, not the private content of employee conversations.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              <li className="flex gap-3"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] mt-2" /> Employers receive aggregate, non-identifying utilization reports showing categories such as visits, phone calls, personal support meetings, and prayer or spiritual support.</li>
              <li className="flex gap-3"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] mt-2" /> Routine employer reports do not include employee names or written accounts of confidential conversations.</li>
              <li className="flex gap-3"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] mt-2" /> If additional professional assistance is needed, a chaplain may recommend your organization's EAP or another appropriate resource.</li>
              <li className="flex gap-3"><span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4A6FA5] mt-2" /> Confidentiality is maintained within applicable ethical and legal limits, including legally required disclosures and serious safety concerns.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[#4A6FA5]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white">Ready when you are.</h2>
          <p className="mt-4 text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto">
            One short form connects you with a chaplain. Your employer will not see who reached out or why.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/care-portal/request-support"
              data-testid="final-cta-request-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#4A6FA5] font-medium hover:bg-blue-50 transition-colors duration-200"
            >
              Request Support
            </Link>
            <a
              href={scheduleTarget}
              target={scheduleExternal ? "_blank" : undefined}
              rel={scheduleExternal ? "noreferrer" : undefined}
              data-testid="final-cta-schedule-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-blue-200 text-white font-medium hover:bg-[#3B597B] transition-colors duration-200"
            >
              <Calendar className="w-4 h-4" />
              Schedule a Chaplain
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
